import { useEffect, useState } from 'react';
import moment from 'moment';
import { Row, Col, Card, Button, Collapse, Space, Tag, Typography, Alert, Table, Tooltip } from 'antd';
import { SyncOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { SUBSCRIPTION_STATUS } from '../../../constants/AppConstants';
import {
  convertCentsIntoDollar,
  formatDate, isSupportAgent2, isSupportLead
} from '../../../helpers/genericHelper';
import LoaderComponent from '../../Loader';
import ActionsMenu from '../../ActionsMenu';
import { PersistedCollapse } from '../../PersistedSections';
import { useParams } from 'react-router';
import API from '../../../services/API';
import PreviewInvoiceComponent from "./Subscription/PreviewInvoiceComponent";
import UpdateSubscriptionModal from "./Subscription/UpdateSubscriptionModal";
import SubscriptionRenewModal from './Subscription/SubscriptionRenewModal';
import SubscriptionResumeModal from './Subscription/SubscriptionResumeModal';
import SubscriptionCancelModal from './Subscription/SubscriptionCancelModal';
import SubscriptionDiscount from './Subscription/SubscriptionDiscount';
import ChargeAndDisputesDetails from './Subscription/ChargeAndDisputesDetails';
import Data from '../../CustomText/data';
import SubscriptionPlans from './Subscription/SubscriptionPlans';
import AvaAssist from './Subscription/AvaAssist';
import PaymentIssues from './Subscription/PaymentIssues';
import StripeCards from './StripeCards';

const { Panel } = Collapse;
const { Text } = Typography;

const OPEN_STATUSES = [
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.WILL_CANCEL,
  SUBSCRIPTION_STATUS.INITIALIZING
];

const statusColor = (status) => {
  switch (status) {
    case SUBSCRIPTION_STATUS.ACTIVE:
      return 'success';
    case SUBSCRIPTION_STATUS.PAST_DUE:
    case SUBSCRIPTION_STATUS.WILL_CANCEL:
      return 'warning';
    case SUBSCRIPTION_STATUS.CANCELLED:
    case SUBSCRIPTION_STATUS.INITIALIZING_FAILED:
      return 'error';
    default:
      return 'default';
  }
};

const invoiceStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'open':
    case 'uncollectible':
      return 'error';
    default:
      return 'default';
  }
};

const toDollars = (cents) => cents ? convertCentsIntoDollar(cents) : null;

const CountTag = ({ count, label, problem = false }) => {
  if (count == null) return null;
  return <Tag color={problem && count > 0 ? "error" : "default"}>{label ? `${count} ${label}` : count}</Tag>;
};

const SummaryCard = ({ title, children, detail, style }) => (
  <Card size="small" className="summary-card" style={style}>
    <Text type="secondary"><small>{title}</small></Text>
    <div><Text strong style={{ fontSize: 18 }}>{children ?? "N/A"}</Text></div>
    {detail && <Text type="secondary"><small>{detail}</small></Text>}
  </Card>
);

function InvoicesTable({ invoices, charges, disputes }) {
  const columns = [
    {
      title: 'Created',
      dataIndex: 'created',
      render: (created) => moment.unix(created).format('YYYY-MM-DD')
    },
    { title: 'Total', dataIndex: 'total', render: (total) => <Text strong>{toDollars(total) ?? '-'}</Text> },
    { title: 'Tax', dataIndex: 'tax', render: (tax) => toDollars(tax) ?? '-' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => <Tag color={invoiceStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Paid',
      dataIndex: 'paidDate',
      render: (paidDate, invoice) => invoice.status === 'paid' && paidDate
        ? formatDate(moment.unix(paidDate).toDate(), true)
        : '-'
    },
    {
      title: 'Invoice ID',
      dataIndex: 'id',
      render: (id) => <Space size={4}>
        <Text copyable>{id}</Text>
        <a href={"https://dashboard.stripe.com/invoices/" + id} title="Open in Stripe" target="_blank" rel="noreferrer">
          <small>open</small>
        </a>
      </Space>
    }
  ];

  const sortedInvoices = [...invoices].sort((a, b) => b.created - a.created);

  return (
    <Table
      className="summary-table"
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={sortedInvoices}
      pagination={sortedInvoices.length > 5 ? { pageSize: 5, size: 'small' } : false}
      locale={{ emptyText: 'No invoices found for this subscription' }}
      expandable={{
        expandedRowRender: (invoice) => {
          const invoiceCharges = charges?.filter((charge) => invoice.id === charge?.invoice) || [];
          if (invoiceCharges.length === 0) return <Text strong>- No charges found -</Text>;
          return invoiceCharges.map((charge) => (
            <ChargeAndDisputesDetails
              key={charge.id}
              charge={charge}
              disputes={Object.values(disputes || {}).map(d => d[0]).filter(d => d?.charge === charge.id)}
            />
          ));
        }
      }}
    />
  );
}

function SubscriptionBlock({
  subscription: data,
  invoices,
  charges,
  disputes,
  latestDecline,
  userId,
  refreshList,
  onCancel,
  onUpdate
}) {
  const subscriptionInvoices = invoices.filter((i) => i.subscription === data?.stripeSubscriptionId);
  const openInvoices = subscriptionInvoices.filter((i) => i.status === 'open').length;
  const canChange = (data.status !== SUBSCRIPTION_STATUS.CANCELLED || !data.subscriptionCancellationTs)
    && data.status !== SUBSCRIPTION_STATUS.INITIALIZING_FAILED;
  const isCancelled = data.status === SUBSCRIPTION_STATUS.CANCELLED || !!data.subscriptionCancellationTs;
  const hasUpcomingInvoice = data.status !== SUBSCRIPTION_STATUS.CANCELLED
    && data.status !== SUBSCRIPTION_STATUS.INITIALIZING_FAILED;
  const isDiscounted = data.undiscountedPriceInCents && data.undiscountedPriceInCents !== data.priceInCents;
  const couponEndsLater = data.couponEndTs && new Date(data.couponEndTs) > new Date();

  const alerts = [
    data.amountPastDueInCents > 0 && {
      type: 'error',
      message: `${toDollars(data.amountPastDueInCents)} past due.${latestDecline
        ? ` Last card decline: ${latestDecline.declineCode || 'unknown'} on ${formatDate(moment(latestDecline.createTs).toDate())}.`
        : ''}`
    },
    data.status === SUBSCRIPTION_STATUS.WILL_CANCEL && {
      type: 'warning',
      message: `Subscription will cancel${data.pendingCancellationTs ? ` on ${formatDate(data.pendingCancellationTs)}` : ''}.`
    },
    couponEndsLater && {
      type: 'info',
      message: `Coupon ends ${formatDate(data.couponEndTs)}.${data.renewalPriceInCents && data.renewalPriceInCents !== data.priceInCents
        ? ` Price goes from ${toDollars(data.priceInCents)} to ${toDollars(data.renewalPriceInCents)}.`
        : ''}`
    }
  ].filter(Boolean);

  const actionGroups = [
    {
      title: 'Plan',
      items: [
        isSupportLead() && canChange && <Button key="update" onClick={() => onUpdate(data)}>Update</Button>,
        <SubscriptionPlans key="offers" userId={userId} refreshList={refreshList} />,
        isSupportAgent2() && data.status === SUBSCRIPTION_STATUS.WILL_CANCEL &&
          <SubscriptionResumeModal key="resume" refreshList={refreshList} userId={userId} subscriptionId={data?.id} />
      ]
    },
    {
      title: 'Danger Zone',
      danger: true,
      items: [
        isSupportAgent2() && canChange && <Button key="cancel" danger onClick={() => onCancel(data)}>Cancel</Button>
      ]
    }
  ];

  return (
    <div className="summary-page">
      {/* Header */}
      <Card size="small" className="summary-header">
        <div className="summary-header-row">
          <div className="summary-avatar"><SyncOutlined /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={8} wrap>
              <Text strong style={{ fontSize: 18 }}>{data?.interval ? `${data.interval} Plan` : 'Subscription'}</Text>
              <Tag color={statusColor(data?.status)}>{data?.status}</Tag>
            </Space>
            <div>
              <Space size={4} wrap split={<Text type="secondary">·</Text>}>
                <Text type="secondary" copyable={!!data?.stripeSubscriptionId}>{data?.stripeSubscriptionId || 'N/A'}</Text>
                {data?.stripeSubscriptionId &&
                  <a href={"https://dashboard.stripe.com/subscriptions/" + data.stripeSubscriptionId} target="_blank" rel="noreferrer">
                    Open in Stripe
                  </a>
                }
                <Text type="secondary">
                  Signed up {data?.subscriptionSignUpTs ? formatDate(data.subscriptionSignUpTs) : 'N/A'}
                </Text>
              </Space>
            </div>
          </div>
          <ActionsMenu groups={actionGroups} />
        </div>
      </Card>

      {alerts.length > 0 &&
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {alerts.map((alert) => <Alert key={alert.message} showIcon type={alert.type} message={alert.message} />)}
        </Space>
      }

      {/* Prices and dates */}
      <div className="summary-cards">
        <SummaryCard title="Price per Interval" detail={data?.interval}>
          {toDollars(data?.priceInCents)}
          {isDiscounted && <span className="summary-price-strike">{toDollars(data.undiscountedPriceInCents)}</span>}
        </SummaryCard>
        <SummaryCard title="Renewal Price">{toDollars(data?.renewalPriceInCents)}</SummaryCard>
        <SummaryCard
          title="Amount Past Due"
          style={data?.amountPastDueInCents > 0 ? { borderLeftColor: '#ff4d4f' } : undefined}>
          {data?.amountPastDueInCents > 0
            ? <Text type="danger" strong style={{ fontSize: 18 }}>{toDollars(data.amountPastDueInCents)}</Text>
            : '$0.00'}
        </SummaryCard>
        <SummaryCard title="Current Period End">
          {data?.currentPeriodEndTs ? formatDate(data.currentPeriodEndTs) : null}
        </SummaryCard>
        <SummaryCard title="Invoices" detail={openInvoices > 0 ? <Text type="danger">{openInvoices} open</Text> : null}>
          {subscriptionInvoices.length}
        </SummaryCard>
      </div>


      {/* Grouped sections */}
      <PersistedCollapse name={`subscription-groups:${data.stripeSubscriptionId}`} className="summary-groups">
        <Panel
          header="Payment History"
          key="billing"
          extra={<Space size={4}>
            <CountTag count={subscriptionInvoices.length} label="invoices" />
            {openInvoices > 0 && <CountTag count={openInvoices} label="open" problem />}
          </Space>}>
          <InvoicesTable invoices={subscriptionInvoices} charges={charges} disputes={disputes} />
          {hasUpcomingInvoice &&
            <PersistedCollapse name={`subscription-upcoming:${data.stripeSubscriptionId}`} ghost style={{ marginTop: 8 }}>
              <Panel header="Preview Upcoming Invoice" key="upcoming">
                <PreviewInvoiceComponent userId={userId} subscriptionId={data.id} />
              </Panel>
            </PersistedCollapse>
          }
        </Panel>
        <Panel
          header="Cancellation and Discount"
          key="cancellation"
          extra={<Space size={4}>
            <Tag color={isCancelled ? 'error' : 'success'}>{isCancelled ? 'Cancelled' : 'Not cancelled'}</Tag>
            <Tag>{data?.subscriptionDiscount ? 'Discount applied' : 'No discount'}</Tag>
          </Space>}>
          <Row gutter={[12, 12]}>
            <Col xs={24} xl={12}>
              <Card size="small" title="Cancellation" className="summary-detail-card">
                <div className="summary-detail-grid">
                  <Data title="Canceled By" value={data?.cancelledBy} />
                  <Data title="Cancelation TS" value={data?.subscriptionCancellationTs ? formatDate(data.subscriptionCancellationTs) : null} />
                  <Data title="Cancelation Reason" value={data?.cancelReason} />
                  <Data title="Subscription Cancellation" value={data?.subsCancellationTs ? formatDate(data.subsCancellationTs, true) : null} />
                  <Data title="Automatic Cancellation" value={data?.pendingCancellationTs ? formatDate(data.pendingCancellationTs, true) : null} />
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={12}>
              <Card size="small" title="Discount and Trial" className="summary-detail-card">
                <SubscriptionDiscount discount={data?.subscriptionDiscount} />
                <div className="summary-detail-grid">
                  <Data title="Coupon (End) Date" value={data?.couponEndTs ? formatDate(data.couponEndTs, true) : null} />
                  <Data title="Undiscounted Price" value={toDollars(data?.undiscountedPriceInCents)} />
                  <Data title="Free Trial (ended date)" value={
                    data?.freeTrial && data?.freeTrialEndTs ? formatDate(data.freeTrialEndTs, true) : "- No Free Trial -"
                  } />
                </div>
              </Card>
            </Col>
          </Row>
        </Panel>
      </PersistedCollapse>
    </div>
  );
}

const cancellationDate = (data) => data?.subscriptionCancellationTs || data?.subsCancellationTs || null;
const isCancelledMembership = (data) => data?.status === SUBSCRIPTION_STATUS.CANCELLED || !!cancellationDate(data);
// Paid membership starts when a free trial ends, otherwise at sign-up
const activationDate = (data) => (data?.freeTrial && data?.freeTrialEndTs) ? data.freeTrialEndTs : data?.subscriptionSignUpTs;
const planName = (data) => data?.interval || data?.subscriptionInterval || data?.subscriptionPlanId || null;
// Intervals like MONTHLY read as "Monthly"; plan ids like creditBuilderLoanSub40 are shown as they are
const planLabel = (data) => {
  const name = planName(data);
  return name && /^[A-Z_]+$/.test(name) ? formatName(name) : name;
};
const planPriceCents = (data) => data?.priceInCents ?? data?.priceCents ?? null;
const formatName = (value) => value
  ? String(value).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
  : null;
const dateOrDash = (date) => date ? formatDate(date) : <Text type="secondary">—</Text>;

// Every membership the user has had, newest first, with the key facts about each
function MembershipSummary({ subscriptions }) {
  const memberships = [...(subscriptions || [])]
    .sort((a, b) => new Date(b.subscriptionSignUpTs || 0) - new Date(a.subscriptionSignUpTs || 0));

  if (memberships.length === 0) return <Text type="secondary">This user has never had a membership.</Text>;

  const columns = [
    {
      title: 'Status',
      key: 'status',
      render: (_, data) => <Tag color={isCancelledMembership(data) ? 'error' : statusColor(data.status)}>
        {isCancelledMembership(data) ? 'Cancelled' : (formatName(data.status) || 'Unknown')}
      </Tag>
    },
    { title: 'Plan', key: 'plan', render: (_, data) => planLabel(data) || <Text type="secondary">—</Text> },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, data) => planPriceCents(data) != null
        ? <>{convertCentsIntoDollar(planPriceCents(data))}{data.interval ? <Text type="secondary"> / {formatName(data.interval).toLowerCase()}</Text> : null}</>
        : <Text type="secondary">—</Text>
    },
    { title: 'Subscribed', key: 'signup', render: (_, data) => dateOrDash(data.subscriptionSignUpTs) },
    {
      title: <Tooltip title="When the paid membership started: the end of the free trial, or the sign-up date if there was no trial">
        Activated
      </Tooltip>,
      key: 'activated',
      render: (_, data) => dateOrDash(activationDate(data))
    },
    { title: 'Cancelled On', key: 'cancelled', render: (_, data) => dateOrDash(cancellationDate(data)) },
    { title: 'Cancelled By', key: 'cancelledBy', render: (_, data) => data.cancelledBy || <Text type="secondary">—</Text> },
    { title: 'Cancellation Reason', key: 'reason', render: (_, data) => data.cancelReason || <Text type="secondary">—</Text> }
  ];

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Text type="secondary"><small><ArrowDownOutlined /> Newest at the top, oldest at the bottom</small></Text>
      <Table
        className="summary-table"
        size="small"
        rowKey={(data) => data.id || data.stripeSubscriptionId}
        columns={columns}
        dataSource={memberships}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={(data) => isCancelledMembership(data) ? 'membership-cancelled-row' : ''}
      />
    </Space>
  );
}

function Subscriptions({ subscriptions, loading, refreshList, charges, disputes, invoices, stripeCards, userId: userIdProp }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [modalLoading, showModalLoading] = useState(false);
  const [isRenewSubscriptionAllowed, setIsRenewSubscriptionAllowed] = useState(false);
  const [winback, setWinback] = useState(null);
  // null until loaded, so the section header doesn't show a count of 0 while loading
  const [paymentIssues, setPaymentIssues] = useState(null);
  const [paymentIssuesError, setPaymentIssuesError] = useState(null);

  let { userId } = useParams();
  userId = userId ?? userIdProp;

  const winBackData = async () => {
    try {
      const winbackOffer = await API.getSubscriptionWinback({ userId })
      setWinback(winbackOffer);
    } catch (error) {
      console.error('Unable to load winback offer', error);
      setWinback(null);
    }
  }

  useEffect(() => {
    setPaymentIssues(null);
    setPaymentIssuesError(null);
    winBackData();
  }, [userId])

  useEffect(() => {
    if (!subscriptions) return;
    setIsRenewSubscriptionAllowed(!subscriptions.some((subscription) => OPEN_STATUSES.includes(subscription.status)));
  }, [subscriptions]);

  const closeModal = () => {
    setModalVisible(false);
  }

  const showCloseSubscriptionModal = (data) => {
    setCurrentSubscriptionId(data?.id)
    setModalVisible(true);
  }

  const showUpdateSubscriptionModal = (data) => {
    setCurrentSubscriptionId(data?.id);
    setUpdateModalVisible(true);
  };

  // Open subscriptions are shown in full; older ones go under Past Subscriptions.
  // With none open, the newest one is shown in full.
  const sortedSubscriptions = [...(subscriptions || [])]
    .sort((a, b) => new Date(b.subscriptionSignUpTs || 0) - new Date(a.subscriptionSignUpTs || 0));
  let currentSubscriptions = sortedSubscriptions.filter((subscription) => OPEN_STATUSES.includes(subscription.status));
  if (currentSubscriptions.length === 0) currentSubscriptions = sortedSubscriptions.slice(0, 1);
  const pastSubscriptions = sortedSubscriptions.filter((subscription) => !currentSubscriptions.includes(subscription));

  const achIssues = paymentIssues?.achPaymentIssues || [];
  const stripeDeclines = paymentIssues?.stripeDeclines || [];
  const latestDecline = [...stripeDeclines].sort((a, b) => new Date(b.createTs) - new Date(a.createTs))[0];
  const paymentIssueCount = paymentIssues ? achIssues.length + stripeDeclines.length : null;
  const disputedCharges = Object.values(disputes || {}).filter((chargeDisputes) => chargeDisputes?.length > 0).length;
  const primaryCard = stripeCards?.find((card) => card.isPrimary);

  const blockProps = {
    invoices: invoices || [],
    charges,
    disputes,
    latestDecline,
    userId,
    refreshList,
    onCancel: showCloseSubscriptionModal,
    onUpdate: showUpdateSubscriptionModal
  };

  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {isSupportLead() && isRenewSubscriptionAllowed &&
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <SubscriptionRenewModal refreshList={refreshList} userId={userId} />
            </div>
          }

          {disputedCharges > 0 &&
            <Alert showIcon type="error" message={`${disputedCharges} disputed ${disputedCharges === 1 ? 'charge' : 'charges'}. See Payment History for details.`} />
          }

          {currentSubscriptions.length > 0
            ? currentSubscriptions.map((data) => (
              <SubscriptionBlock key={data.stripeSubscriptionId} subscription={data} {...blockProps} />
            ))
            : <Row className="no-data-card">No Data Found</Row>}

          {/* Sections about the customer rather than one subscription */}
          <div className="summary-page">
            <PersistedCollapse name="subscription-page-sections" className="summary-groups" defaultActiveKey={['summary']}>
              <Panel
                header="Summary"
                key="summary"
                extra={<Space size={4}>
                  <CountTag count={subscriptions?.length ?? 0} label={subscriptions?.length === 1 ? 'membership' : 'memberships'} />
                  <CountTag
                    count={(subscriptions || []).filter(isCancelledMembership).length}
                    label="cancelled"
                    problem />
                </Space>}>
                <MembershipSummary subscriptions={subscriptions} />
              </Panel>
              <Panel
                header="Payment Issues"
                key="payment-issues"
                forceRender
                extra={paymentIssuesError
                  ? <Tag color="error">Couldn't load</Tag>
                  : <CountTag count={paymentIssueCount} label="issues" problem />}>
                <PaymentIssues
                  userId={userId}
                  onLoaded={(issues) => { setPaymentIssues(issues); setPaymentIssuesError(null); }}
                  onError={setPaymentIssuesError} />
              </Panel>
              <Panel
                header="Stripe Cards"
                key="payment-methods"
                extra={<Space size={4}>
                  <CountTag count={stripeCards?.length ?? 0} label="cards" />
                  {primaryCard && <Tag>{primaryCard.cardBrand} •{primaryCard.last4} primary</Tag>}
                </Space>}>
                <StripeCards userId={userId} refreshData={refreshList} stripeCards={stripeCards || []} loading={loading} />
              </Panel>
              <Panel
                header="Winback Offer"
                key="winback"
                collapsible={winback?.copy ? undefined : 'disabled'}
                extra={<Tag>{winback?.copy ? 'Available' : 'None'}</Tag>}>
                {winback?.copy && <Row gutter={[24, 12]}>
                  <Col xs={24} xl={12}>
                    <Typography.Title level={4}>{winback.copy.title}</Typography.Title>
                    <Typography.Text>{winback.copy.description}</Typography.Text>
                  </Col>
                  <Col xs={24} xl={12}>
                    {winback.plans?.length > 0 && <>
                      <Typography.Title level={5}>Available Plans</Typography.Title>
                      <ol>
                        {winback.plans.map((plan) => <li key={plan.id}>
                          <Typography.Title level={5}><Typography.Text type="danger" delete>{plan.originalPaymentTermText}</Typography.Text> {plan.billedText}</Typography.Title>
                          <Typography.Text>{plan.description}</Typography.Text>
                        </li>)}
                      </ol>
                    </>}
                  </Col>
                </Row>}
              </Panel>
              <Panel header="Ava Assist" key="ava-assist">
                <AvaAssist userId={userId} />
              </Panel>
              {pastSubscriptions.length > 0 &&
                <Panel
                  header="Past Subscriptions"
                  key="past"
                  extra={<CountTag count={pastSubscriptions.length} />}>
                  <PersistedCollapse name="subscription-past" ghost>
                    {pastSubscriptions.map((data) => (
                      <Panel
                        key={data.stripeSubscriptionId}
                        header={<Space size={8} wrap>
                          <Text strong>{data.interval ? `${data.interval} Plan` : 'Subscription'}</Text>
                          <Tag color={statusColor(data.status)}>{data.status}</Tag>
                          <Text type="secondary">
                            {data.subscriptionSignUpTs ? formatDate(data.subscriptionSignUpTs) : 'N/A'}
                            {' – '}
                            {(data.subsCancellationTs || data.subscriptionCancellationTs)
                              ? formatDate(data.subsCancellationTs || data.subscriptionCancellationTs)
                              : 'N/A'}
                          </Text>
                        </Space>}>
                        <SubscriptionBlock subscription={data} {...blockProps} />
                      </Panel>
                    ))}
                  </PersistedCollapse>
                </Panel>
              }
            </PersistedCollapse>
          </div>
        </Space>
      )}

      <SubscriptionCancelModal
        modalVisible={modalVisible}
        closeModal={closeModal}
        userId={userId}
        currentSubscriptionId={currentSubscriptionId}
        refreshList={refreshList}
        setModalVisible={setModalVisible}
        modalLoading={modalLoading}
        showModalLoading={showModalLoading} />

      {updateModalVisible && (
          <UpdateSubscriptionModal
              userId={userId}
              activeSubscriptionId={currentSubscriptionId}
              onClose={() => setUpdateModalVisible(false)}
              refreshList={refreshList}
          />
      )}

    </>

  );
}

export default Subscriptions;
